// DocumentsTab — the upload + library surface inside Knowledge.
//
// Drop zone + per-format parsers (run client-side in the WebKit, no upload
// before parse) → team-wide library fetched from the Worker → chunked +
// embedded + indexed in Vectorize via /docs. Binary assets (images, PDF
// source) land in R2 keyed by docId. The right-hand preview pane lazily
// hydrates full text + image blobs from the Worker when a doc is selected.
//
// Rendered as the "Documents" tab inside IntelligencePage. Old /knowledge
// URL still lands here via the IntelligencePage tab parent.

import { useEffect, useRef, useState } from "react";

import { useAuth } from "../../components/AuthGate";
import {
  detectKind,
  parseDocument,
  type DocumentKind,
  type ParsedDocument,
} from "../../lib/document-parsers";
import {
  deleteDocument,
  fetchBlobAsDataUrl,
  fetchDocument,
  fetchDocuments,
  uploadDocument,
  type DocSummary,
} from "../../lib/documents-sync";

type ParseState =
  | { kind: "idle" }
  | { kind: "parsing"; fileName: string }
  | { kind: "syncing"; fileName: string; chunkCount?: number }
  | { kind: "error"; fileName: string; message: string }
  | { kind: "done"; doc: ParsedDocument; chunkCount: number };

/** A doc as displayed locally — may be a "summary" stub fetched from the
 * Worker (with text/sections empty until hydrated) or a fully parsed local
 * doc. The `_synced` flag distinguishes the two for UI affordances. */
interface LocalDoc extends ParsedDocument {
  _synced: boolean;
  _chunkCount?: number;
  _uploadedBy?: string;
}

const ACCEPT =
  ".xlsx,.xls,.xlsm,.csv,.tsv,.docx,.pdf,.pptx,.html,.htm,.txt,.md,.png,.jpg,.jpeg,.gif,.webp";

function summaryToLocalDoc(s: DocSummary): LocalDoc {
  return {
    id: s.id,
    fileName: s.fileName,
    kind: s.kind,
    charCount: s.charCount,
    text: "",
    sections: [],
    pulledAt: s.pulledAt,
    bytes: s.bytes,
    image: s.image
      ? { dataUrl: "", width: s.image.width, height: s.image.height, mimeType: s.image.mimeType }
      : undefined,
    _synced: true,
    _chunkCount: s.chunkCount,
    _uploadedBy: s.uploadedBy,
  };
}

/**
 * @param hideHeader  When rendered inside IntelligencePage as a tab, the
 * parent owns the page header. Set hideHeader to suppress the duplicate
 * title bar.
 */
export function DocumentsTab({ hideHeader = true }: { hideHeader?: boolean } = {}) {
  const { session } = useAuth();
  const [state, setState] = useState<ParseState>({ kind: "idle" });
  const [docs, setDocs] = useState<LocalDoc[]>([]);
  const [dragging, setDragging] = useState(false);
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [hydratingDocId, setHydratingDocId] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // On mount: pull the team-wide doc list from the Worker. We seed `docs`
  // with metadata-only stubs; full text gets fetched lazily when the user
  // selects a doc (so we don't drag down the whole library at startup).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const summaries = await fetchDocuments();
        if (cancelled) return;
        setDocs(summaries.map(summaryToLocalDoc));
        setListError(null);
      } catch (e) {
        console.error("[knowledge] fetchDocuments failed", e);
        if (!cancelled) {
          setListError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleFile(file: File) {
    console.log("[knowledge] parsing", file.name, file.type, file.size);
    setState({ kind: "parsing", fileName: file.name });
    try {
      const doc = await parseDocument(file);
      console.log(
        "[knowledge] parsed",
        file.name,
        "→",
        doc.sections.length,
        "sections,",
        doc.charCount,
        "chars",
      );
      // Optimistic: show it in the list right away, unsynced.
      const optimistic: LocalDoc = { ...doc, _synced: false };
      setDocs((prev) => [optimistic, ...prev]);
      setActiveDocId(doc.id);
      setState({ kind: "syncing", fileName: file.name });

      // Write-through to Worker — chunk + embed + Vectorize + R2.
      const summary = await uploadDocument(doc, session?.email);
      console.log(
        "[knowledge] synced",
        file.name,
        "→",
        summary.chunkCount,
        "chunks",
      );
      setDocs((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? { ...d, _synced: true, _chunkCount: summary.chunkCount, _uploadedBy: summary.uploadedBy }
            : d,
        ),
      );
      setState({ kind: "done", doc, chunkCount: summary.chunkCount ?? 0 });
    } catch (e) {
      console.error("[knowledge] parse/sync failed for", file.name, e);
      const detail =
        e instanceof Error
          ? `${e.message}\n\n${e.stack ?? "(no stack)"}`
          : String(e);
      setState({
        kind: "error",
        fileName: file.name,
        message: detail,
      });
    }
  }

  // Lazily hydrate a doc when the user selects it — pull full sections/text
  // from the Worker if we only have the summary stub. For image docs, also
  // pull the binary from R2 and reconstruct the data URL so the preview
  // pane can render an inline thumbnail.
  async function hydrate(id: string) {
    const target = docs.find((d) => d.id === id);
    if (!target) return;
    const needsText = target.sections.length === 0;
    const needsBlob =
      target.kind === "image" && target.image && !target.image.dataUrl;
    if (!needsText && !needsBlob) return;
    setHydratingDocId(id);
    try {
      if (needsText) {
        const full = await fetchDocument(id);
        if (full) {
          setDocs((prev) =>
            prev.map((d) =>
              d.id === id
                ? {
                    ...d,
                    text: full.text,
                    sections: full.sections,
                    charCount: full.charCount,
                  }
                : d,
            ),
          );
        }
      }
      if (needsBlob && target.image?.mimeType) {
        const dataUrl = await fetchBlobAsDataUrl(id, target.image.mimeType);
        if (dataUrl) {
          setDocs((prev) =>
            prev.map((d) =>
              d.id === id && d.image
                ? { ...d, image: { ...d.image, dataUrl } }
                : d,
            ),
          );
        }
      }
    } catch (e) {
      console.error("[knowledge] hydrate failed", id, e);
    } finally {
      setHydratingDocId(null);
    }
  }

  async function handleSelect(id: string) {
    setActiveDocId(id);
    await hydrate(id);
  }

  async function handleDelete(id: string) {
    if (!confirm("Remove this document from the team library? This wipes its embeddings and bytes.")) return;
    const snapshot = docs;
    setDocs((prev) => prev.filter((d) => d.id !== id));
    if (activeDocId === id) setActiveDocId(null);
    try {
      await deleteDocument(id);
    } catch (e) {
      // Roll back on failure.
      console.error("[knowledge] delete failed", id, e);
      setDocs(snapshot);
      alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function handleFiles(files: FileList | File[]) {
    const arr = Array.from(files);
    console.log("[knowledge] received", arr.length, "file(s):", arr.map((f) => f.name));
    for (const file of arr) {
      await handleFile(file);
    }
  }

  const activeDoc = docs.find((d) => d.id === activeDocId) ?? docs[0];

  return (
    <>
      {!hideHeader && (
        <div className="page-head">
          <div>
            <div className="page-head__meta">KNOWLEDGE · DOCUMENT INTELLIGENCE</div>
            <h1>Knowledge</h1>
          </div>
        </div>
      )}

      <p style={lede}>
        Drop in any spreadsheet, Word doc, PDF, slide deck, image or plain-text
        note. Parsing happens locally; once it's done, the parsed text and any
        binary assets ship to the shared Worker — chunked, embedded, and indexed
        so the whole ManCo can search the same library. Phase 3 (next) wires a
        chat surface that asks questions across every uploaded document.
      </p>

      <div
        style={dropZone(dragging || state.kind === "parsing")}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = "copy";
          setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
          console.log("[knowledge] drop event, files:", e.dataTransfer.files.length);
          if (e.dataTransfer.files.length > 0) {
            void handleFiles(e.dataTransfer.files);
          }
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          style={{ display: "none" }}
          onChange={(e) => {
            console.log("[knowledge] file input change, files:", e.target.files?.length ?? 0);
            if (e.target.files && e.target.files.length > 0) {
              void handleFiles(e.target.files);
            }
            // Reset so re-selecting the same file fires onChange again
            e.target.value = "";
          }}
        />
        <div style={dropZoneInner}>
          <div style={dropZoneTitle}>
            {state.kind === "parsing"
              ? `Parsing ${state.fileName}…`
              : state.kind === "syncing"
                ? `Syncing ${state.fileName} to the team library…`
                : "Drop documents here or click to browse"}
          </div>
          <div style={dropZoneSub}>
            Excel · CSV · Word · PDF · PowerPoint · HTML · plain text · markdown · PNG · JPG
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            style={chooseBtn}
          >
            Choose files
          </button>
        </div>
      </div>

      <StatusBar state={state} docCount={docs.length} />

      {state.kind === "error" && (
        <div style={errorBar}>
          <div style={{ marginBottom: 6 }}>
            <span style={mono}>PARSE FAILED ·</span> {state.fileName}
          </div>
          <pre style={errorStack}>{state.message}</pre>
        </div>
      )}

      {listError && (
        <div style={errorBar}>
          <div style={{ marginBottom: 6 }}>
            <span style={mono}>TEAM LIBRARY · LIST FAILED</span>
          </div>
          <pre style={errorStack}>{listError}</pre>
        </div>
      )}

      {docs.length > 0 && (
        <div style={layout}>
          <aside style={docList}>
            <div style={docListHead}>TEAM LIBRARY · {docs.length}</div>
            {docs.map((d) => (
              <div
                key={d.id}
                style={docRow(d.id === activeDoc?.id)}
                onClick={() => void handleSelect(d.id)}
                role="button"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={docKindBadge(d.kind)}>{d.kind.toUpperCase()}</span>
                  {!d._synced && <span style={syncChip("pending")}>SYNCING</span>}
                  {d._synced && d._chunkCount != null && d._chunkCount > 0 && (
                    <span style={syncChip("indexed")}>{d._chunkCount} CHUNK{d._chunkCount === 1 ? "" : "S"}</span>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(d.id);
                    }}
                    style={deleteBtn}
                    title="Remove from team library"
                  >
                    ×
                  </button>
                </div>
                <span style={docName} title={d.fileName}>
                  {d.fileName}
                </span>
                <span style={docMeta}>
                  {d.sections.length > 0
                    ? `${d.sections.length} ${d.sections.length === 1 ? "section" : "sections"} · `
                    : ""}
                  {fmtBytes(d.bytes)}
                  {d._uploadedBy ? ` · ${d._uploadedBy}` : ""}
                </span>
              </div>
            ))}
          </aside>

          <main style={docView}>
            {activeDoc ? (
              activeDoc.sections.length === 0 && hydratingDocId === activeDoc.id ? (
                <div style={empty}>Loading {activeDoc.fileName}…</div>
              ) : (
                <DocPreview doc={activeDoc} />
              )
            ) : (
              <div style={empty}>Select a document</div>
            )}
          </main>
        </div>
      )}

      <div style={phaseHint}>
        <span style={mono}>PHASE 2 · WORKER-BACKED LIBRARY</span> · Drops parse
        locally, then ship to the bvndle-notes Worker. Text gets chunked,
        embedded via Workers AI, and indexed in Vectorize. Phase 3 (Anthropic
        chat) lands next.
      </div>
    </>
  );
}

function DocPreview({ doc }: { doc: ParsedDocument }) {
  const headerSize =
    doc.kind === "image" && doc.image
      ? `${doc.image.width}×${doc.image.height} · ${fmtBytes(doc.bytes)}`
      : `${fmtBytes(doc.bytes)} · ${doc.charCount.toLocaleString()} chars`;
  return (
    <div>
      <div style={docHead}>
        <div>
          <div style={docHeadEyebrow}>{doc.kind.toUpperCase()} · {headerSize}</div>
          <div style={docHeadTitle}>{doc.fileName}</div>
        </div>
        <div style={docHeadMeta}>
          parsed {new Date(doc.pulledAt).toLocaleString()}
        </div>
      </div>

      {doc.kind === "image" && doc.image && (
        <div style={imagePreviewWrap}>
          <img src={doc.image.dataUrl} alt={doc.fileName} style={imagePreview} />
          <div style={imageCaption}>
            Vision analysis lands in Phase 3 — the LLM will read this image
            when you ask a question that references it.
          </div>
        </div>
      )}

      <div style={sectionsWrap}>
        {doc.sections.map((s, i) => (
          <details key={i} style={sectionCard} open={i < 2}>
            <summary style={sectionSummary}>
              <span style={sectionLabel}>{s.label}</span>
              <span style={sectionLen}>{s.text.length.toLocaleString()} chars</span>
            </summary>
            <pre style={sectionText}>{s.text.slice(0, 4000)}{s.text.length > 4000 ? "\n\n… truncated for preview" : ""}</pre>
          </details>
        ))}
      </div>
    </div>
  );
}

// StatusBar — always visible. Tells the operator (a) whether the parser is
// idle / running / errored, and (b) how many docs are in the local session.
// This is the answer to "the icon lit up but nothing else happened" — even
// before any document lands, the bar shows "READY · 0 documents" so the
// surface is never silent.
function StatusBar({ state, docCount }: { state: ParseState; docCount: number }) {
  const tone =
    state.kind === "error"
      ? "var(--coral)"
      : state.kind === "parsing" || state.kind === "syncing"
        ? "var(--purple)"
        : state.kind === "done"
          ? "var(--mint, #5ec98c)"
          : "var(--ink-faint)";
  const label =
    state.kind === "error"
      ? `ERROR · ${state.fileName}`
      : state.kind === "parsing"
        ? `PARSING · ${state.fileName}`
        : state.kind === "syncing"
          ? `SYNCING · ${state.fileName}`
          : state.kind === "done"
            ? `SYNCED · ${state.doc.fileName} · ${state.chunkCount} CHUNK${state.chunkCount === 1 ? "" : "S"} INDEXED`
            : "READY";
  return (
    <div style={{ ...statusBar, borderLeftColor: tone }}>
      <span style={{ ...statusDot, background: tone }} />
      <span style={{ ...statusLabel, color: tone }}>{label}</span>
      <span style={statusMeta}>
        {docCount} {docCount === 1 ? "document" : "documents"} in the team library
      </span>
    </div>
  );
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------- styles --------------------------------------------

const lede: React.CSSProperties = {
  maxWidth: 760,
  fontSize: 13,
  color: "var(--ink-dim)",
  lineHeight: 1.6,
  marginBottom: 22,
};

function dropZone(active: boolean): React.CSSProperties {
  return {
    display: "block",
    border: `1.5px dashed ${active ? "var(--purple)" : "var(--edge)"}`,
    background: active ? "rgba(83, 74, 183, 0.06)" : "transparent",
    borderRadius: 8,
    padding: "26px 24px",
    cursor: "pointer",
    transition: "background 120ms ease, border-color 120ms ease",
  };
}
const dropZoneInner: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
  alignItems: "center",
  textAlign: "center",
};
const dropZoneTitle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: "var(--ink)",
};
const dropZoneSub: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  color: "var(--ink-faint)",
  textTransform: "uppercase",
};
const chooseBtn: React.CSSProperties = {
  marginTop: 10,
  padding: "7px 14px",
  background: "transparent",
  border: "1px solid var(--purple)",
  borderRadius: 4,
  color: "var(--purple)",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  fontWeight: 700,
  cursor: "pointer",
};

const statusBar: React.CSSProperties = {
  marginTop: 14,
  padding: "9px 12px",
  border: "1px solid var(--edge)",
  borderLeft: "3px solid var(--ink-faint)",
  borderRadius: 4,
  background: "var(--bg-1, rgba(255,255,255,0.02))",
  display: "flex",
  alignItems: "center",
  gap: 10,
};
const statusDot: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: "50%",
};
const statusLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.14em",
  fontWeight: 700,
  textTransform: "uppercase",
};
const statusMeta: React.CSSProperties = {
  marginLeft: "auto",
  fontFamily: "var(--font-mono)",
  fontSize: 10,
  letterSpacing: "0.08em",
  color: "var(--ink-faint)",
  textTransform: "uppercase",
};

const errorBar: React.CSSProperties = {
  marginTop: 14,
  padding: "10px 12px",
  borderLeft: "3px solid var(--coral)",
  background: "rgba(196, 74, 74, 0.08)",
  color: "var(--coral)",
  fontSize: 12,
  borderRadius: 3,
};
const errorStack: React.CSSProperties = {
  margin: 0,
  padding: "6px 8px",
  background: "rgba(0,0,0,0.35)",
  color: "var(--ink-dim)",
  fontFamily: "var(--font-mono)",
  fontSize: 10.5,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 240,
  overflow: "auto",
  borderRadius: 2,
};

const layout: React.CSSProperties = {
  marginTop: 22,
  display: "grid",
  gridTemplateColumns: "280px 1fr",
  gap: 18,
  alignItems: "flex-start",
};
const docList: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  border: "1px solid var(--edge)",
  borderRadius: 4,
  overflow: "hidden",
};
const docListHead: React.CSSProperties = {
  padding: "9px 12px",
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.14em",
  color: "var(--ink-faint)",
  fontWeight: 700,
  textTransform: "uppercase",
  borderBottom: "1px solid var(--edge)",
  background: "var(--bg-1, rgba(255,255,255,0.03))",
};

function docRow(active: boolean): React.CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: "10px 12px",
    background: active ? "rgba(83, 74, 183, 0.10)" : "transparent",
    border: "none",
    borderBottom: "0.5px solid var(--edge)",
    borderLeft: `3px solid ${active ? "var(--purple)" : "transparent"}`,
    color: "inherit",
    textAlign: "left",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background 100ms ease",
  };
}
function syncChip(state: "pending" | "indexed"): React.CSSProperties {
  const color = state === "pending" ? "#d99c3a" : "#5ec98c";
  return {
    fontFamily: "var(--font-mono)",
    fontSize: 8.5,
    letterSpacing: "0.12em",
    fontWeight: 700,
    color,
    border: `1px solid ${color}66`,
    background: `${color}1c`,
    padding: "1px 6px",
    borderRadius: 3,
  };
}
const deleteBtn: React.CSSProperties = {
  marginLeft: "auto",
  background: "transparent",
  border: "none",
  color: "var(--ink-faint)",
  cursor: "pointer",
  fontSize: 16,
  lineHeight: 1,
  padding: "0 4px",
  borderRadius: 3,
};
function docKindBadge(kind: DocumentKind): React.CSSProperties {
  const palette: Partial<Record<DocumentKind, string>> = {
    xlsx: "#5ec98c",
    csv: "#5ec98c",
    docx: "#4fb3ff",
    pdf: "#c44a4a",
    pptx: "#d99c3a",
    html: "#b07cff",
    image: "#3aa8d9",
    txt: "var(--ink-faint)",
  };
  const color = palette[kind] ?? "var(--ink-faint)";
  return {
    alignSelf: "flex-start",
    fontFamily: "var(--font-mono)",
    fontSize: 8.5,
    letterSpacing: "0.12em",
    fontWeight: 700,
    color,
    border: `1px solid ${color}66`,
    background: `${color}22`,
    padding: "1px 6px",
    borderRadius: 3,
  };
}
const docName: React.CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--ink)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const docMeta: React.CSSProperties = {
  fontSize: 10.5,
  color: "var(--ink-faint)",
};

const docView: React.CSSProperties = {
  border: "1px solid var(--edge)",
  borderRadius: 4,
  padding: "16px 18px",
  background: "var(--bg-1, rgba(255,255,255,0.02))",
};
const empty: React.CSSProperties = {
  padding: 40,
  textAlign: "center",
  color: "var(--ink-faint)",
};

const docHead: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  borderBottom: "1px solid var(--edge)",
  paddingBottom: 10,
  marginBottom: 12,
};
const docHeadEyebrow: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9,
  letterSpacing: "0.14em",
  color: "var(--ink-faint)",
  fontWeight: 700,
  textTransform: "uppercase",
  marginBottom: 4,
};
const docHeadTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 700,
  color: "var(--ink)",
};
const docHeadMeta: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  letterSpacing: "0.08em",
  color: "var(--ink-faint)",
};

const imagePreviewWrap: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginBottom: 14,
  padding: 12,
  border: "1px solid var(--edge)",
  borderRadius: 4,
  background: "var(--panel-2)",
};
const imagePreview: React.CSSProperties = {
  display: "block",
  maxWidth: "100%",
  maxHeight: 420,
  margin: "0 auto",
  borderRadius: 3,
  border: "1px solid var(--edge)",
};
const imageCaption: React.CSSProperties = {
  fontSize: 11.5,
  color: "var(--ink-faint)",
  textAlign: "center",
  fontStyle: "italic",
  lineHeight: 1.5,
};

const sectionsWrap: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};
const sectionCard: React.CSSProperties = {
  border: "1px solid var(--edge)",
  borderRadius: 4,
  background: "var(--panel-2)",
};
const sectionSummary: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: 12,
};
const sectionLabel: React.CSSProperties = {
  fontWeight: 600,
  color: "var(--ink)",
};
const sectionLen: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  letterSpacing: "0.08em",
  color: "var(--ink-faint)",
};
const sectionText: React.CSSProperties = {
  margin: 0,
  padding: "8px 12px 12px",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  color: "var(--ink-dim)",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 280,
  overflow: "auto",
  borderTop: "0.5px solid var(--edge)",
};

const phaseHint: React.CSSProperties = {
  marginTop: 26,
  padding: "10px 14px",
  border: "1px dashed var(--edge)",
  borderRadius: 4,
  fontSize: 11.5,
  color: "var(--ink-dim)",
  lineHeight: 1.5,
};

const mono: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  letterSpacing: "0.14em",
  fontWeight: 700,
  textTransform: "uppercase",
  color: "var(--purple)",
};
